import { useEffect, useState } from "react";
import { CheckSquare, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { lastFirst } from "@/lib/names";

// Job wrapped up for everyone: end every active assignment and complete the project in one step.
export default function CompleteProjectModal({ project: p, onClose, onDone }) {
  const [active, setActive] = useState(null); // [{id, worker_name, pay_rate}]
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/assignments", { params: { project_id: p.id } }), api.get("/workers")])
      .then(([a, w]) => {
        const names = new Map((w.data || []).map((x) => [x.id, x.name]));
        setActive((a.data || []).filter((x) => x.project_id === p.id && x.status === "active").map((x) => ({ ...x, worker_name: names.get(x.worker_id) || "—" })));
      })
      .catch((e) => { toast.error(e.response?.data?.detail?.toString() || e.message); setActive([]); });
  }, [p.id]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/projects/${p.id}/complete-all`, { end_date: endDate });
      toast.success(`"${data.project_name}" marked completed${data.ended_assignments ? ` · ${data.ended_assignments} assignment${data.ended_assignments === 1 ? "" : "s"} ended ${data.end_date}` : ""}.`);
      onDone(data);
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="complete-project-modal" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold flex items-center gap-2"><CheckSquare size={22} weight="duotone" className="text-emerald-700" /> End all workers &amp; complete project</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-600">Use this when the job at <b>{p.name}</b> has wrapped up for everyone. Every active assignment ends on the same last day and the project is marked <b>completed</b>.</p>

        <label className="block mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Last day worked
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="complete-project-end-date" className="mt-1 block w-48 h-11 px-3 border border-slate-300 rounded-md" />
        </label>

        <div className="mt-4 border border-slate-200 rounded-md bg-slate-50 p-3" data-testid="complete-project-workers">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
            {active == null ? "Loading…" : active.length === 0 ? "No active workers — the project will simply be marked completed" : `${active.length} active assignment${active.length === 1 ? "" : "s"} will end`}
          </div>
          {active && active.length > 0 && (
            <ul className="text-sm text-slate-800 divide-y divide-slate-200">
              {active.map((a) => <li key={a.id} className="py-1 flex justify-between"><span className="font-semibold">{lastFirst(a.worker_name)}</span><span className="text-xs text-slate-500">since {a.start_date || "—"}</span></li>)}
            </ul>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-3">Workers with no other active assignment are set to inactive automatically. You can reactivate the project later from the Projects tab if needed.</p>
        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={busy} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button data-testid="complete-project-confirm" disabled={busy || active == null} className="h-11 px-4 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-md disabled:opacity-60">
            {busy ? "Working…" : (active && active.length ? `End ${active.length} & complete` : "Mark completed")}
          </button>
        </div>
      </form>
    </div>
  );
}
